/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import Listado from 'components/public/Listado/Listado';
import { useNavigate } from 'react-router-dom';
import { titulosListadoAlquileres } from 'utils/constants';
import NoResponse from 'pages/auxiliares/NoResponse';
import NotAllowed from 'pages/auxiliares/NotAllowed';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'utils/redux/store/store';
import { fetchAlquileres } from 'utils/redux/features/alquileresSlice';
import { AlquilerBase } from 'utils/models/types';
import { getSalaPorId } from 'services/servicioGet';
import { formatearDateAFechaHoraUY } from 'utils/utilities';
import { alquileresPorFecha } from 'services/servicioPost';
import { FechaString } from 'utils/models/interfaces';
import { useError } from 'services/ErrorContext';

const Alquileres = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { alquileres, loadingAlq, errorAlq } = useSelector((state: RootState) => state.alquileres);
  const error401 = errorAlq?.includes('permisos');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [alquileresFiltrados, setAlquileresFiltrados] = useState<AlquilerBase[]>(alquileres);
  const { error, setError, clearError, loading, setLoading } = useError();

  useEffect(() => {
    dispatch(fetchAlquileres());
  }, [dispatch]);

  useEffect(() => {
    if (startDate && endDate) {
      handleGetAlquileres();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    handleGetAlquileres();
  }, [alquileres]);

  const getSala = async (salaId: number) => {
    if (salaId) {
      const sala = await getSalaPorId(String(salaId));
      return sala;
    }
    return null;
  };

  const handleGetAlquileres = async () => {
    try {
      const listaAlquileres = await armarListado(alquileres);
      setAlquileresFiltrados(listaAlquileres);
    } catch (error) {
      console.error('Error obteniendo alquileres:', error);
    }
  };

  const armarListado = async (alquileres: AlquilerBase[]): Promise<AlquilerBase[]> => {
    return alquileres
      ? await Promise.all(
          alquileres.map(async (a: AlquilerBase) => {
            const sala = a.SalaId ? await getSala(a.SalaId) : null;
            const nombreSala = sala ? sala.Nombre : '';

            return {
              ...a,
              Telefono: a.Telefono || '-',
              NombreSala: nombreSala,
              FechaInicio: a.Fecha && formatearDateAFechaHoraUY(a.Fecha.FechaInicio),
              FechaFin: a.Fecha && formatearDateAFechaHoraUY(a.Fecha.FechaFin),
              Monto: `$${a.Monto}`,
            };
          })
        )
      : [];
  };

  const onAgregar = () => {
    navigate(`/admin/alta-alquiler`);
  };

  const convertDateRange = (dateRange: { startDate: string; endDate: string }) => {
    return {
      Id: 0,
      FechaInicio: new Date(startDate).toISOString(),
      FechaFin: new Date(endDate).toISOString(),
    };
  };

  const filtrarPorFechas = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    clearError();

    if (!startDate || !endDate) {
      setError('Debes seleccionar ambas fechas para filtrar');
      return;
    }

    const requestFiltroFechas: FechaString = convertDateRange({ startDate, endDate });

    try {
      const response = await alquileresPorFecha(requestFiltroFechas, setError, setLoading);

      if (response) {
        const resultados = await Promise.all(
          response.map(async (a: AlquilerBase) => {
            const sala = a.SalaId ? await getSala(a.SalaId) : null;
            const nombreSala = sala ? sala.Nombre : '';

            return {
              ...a,
              Telefono: a.Telefono || '-',
              Sala: nombreSala,
              FechaInicio: a.Fecha ? formatearDateAFechaHoraUY(a.Fecha.FechaInicio) : undefined,
              FechaFin: a.Fecha ? formatearDateAFechaHoraUY(a.Fecha.FechaFin) : undefined,
              Monto: `$${a.Monto}`,
            };
          })
        );

        setAlquileresFiltrados(resultados);
      }
    } catch (error) {
      console.error('Error de envío:', error);
      setError('Ocurrió un error al filtrar por fechas');
    }
  };

  const onEliminar = (id: number) => {
    navigate(`/admin/alquiler/${id}/eliminar`);
  };

  const handleLimpiarFechas = () => {
    setStartDate('');
    setEndDate('');
    handleGetAlquileres();
  };

  return errorAlq || error ? (
    error401 ? (
      <NotAllowed />
    ) : (
      <NoResponse />
    )
  ) : (
    <Listado
      lista={alquileresFiltrados}
      headCells={titulosListadoAlquileres}
      titulo="Gestionar alquileres"
      onAgregar={onAgregar}
      onFiltrar={filtrarPorFechas}
      startDate={startDate}
      endDate={endDate}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      onLimpiarFechas={handleLimpiarFechas}
      loading={loadingAlq || loading}
      onEliminar={onEliminar}
    />
  );
};

export default Alquileres;
